import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, DollarSign, ArrowRight, Leaf, Send, Clock,
  CheckCircle, Star, Zap, Calendar, BarChart2
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import { formatCurrency, parseAccountBalance } from '../utils/helpers';
import axios from 'axios';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';

const API_BASE = 'https://tradebot-k4nt.onrender.com/api';

// Map planNumber -> plan metadata for display enrichment
const PLAN_META = {
  '1000': { roi: '5%',  monthly: '$50',   label: '$1K Plan',   color: 'from-blue-500 to-blue-600',     badge: null },
  '1500': { roi: '7%',  monthly: '$105',  label: '$1.5K Plan', color: 'from-purple-500 to-purple-600',  badge: null },
  '2000': { roi: '9%',  monthly: '$180',  label: '$2K Plan',   color: 'from-indigo-500 to-indigo-600',  badge: 'Most Popular' },
  '2500': { roi: '11%', monthly: '$275',  label: '$2.5K Plan', color: 'from-violet-500 to-violet-600',  badge: null },
  '3000': { roi: '14%', monthly: '$420',  label: '$3K Plan',   color: 'from-fuchsia-500 to-fuchsia-600',badge: 'Premium' },
  '3500': { roi: '18%', monthly: '$630',  label: '$3.5K Plan', color: 'from-pink-500 to-pink-600',      badge: 'Max Returns' },
};

const PeneInvestmentDashboard = () => {
  const navigate = useNavigate();
  const { user: contextUser, fetchUserPublic } = useUser();
  const [user, setUser] = useState(null);
  const [peneBalance, setPeneBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [growLoading, setGrowLoading] = useState(false);
  const [balanceRefreshing, setBalanceRefreshing] = useState(false);
  const [lastGrowTime, setLastGrowTime] = useState(null);
  const [canGrow, setCanGrow] = useState(true);
  const [activePlans, setActivePlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [now, setNow] = useState(Date.now());

  // tick every minute for countdown
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!contextUser?.telegramName) { navigate('/login'); return; }
    setUser(contextUser);
    // Exact same concept as PeneExchange — use fetchUserPublic and read investmentBalance
    fetchUserPublic(contextUser.telegramName).then(info => {
      if (info?.accountBalance) setUser(prev => prev ? { ...prev, accountBalance: info.accountBalance } : prev);
      if (info?.investmentBalance) setPeneBalance(parseFloat(info.investmentBalance) || 0);
      setLoading(false);
    });
    fetchActivePlans(contextUser.telegramName);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextUser?.telegramName, navigate]);

  const fetchPeneBalance = async (username, showRefreshing = false) => {
    if (showRefreshing) setBalanceRefreshing(true);
    try {
      // Same as PeneExchange: fetchUserPublic and read investmentBalance
      const info = await fetchUserPublic(username);
      if (info?.investmentBalance !== undefined) {
        setPeneBalance(parseFloat(info.investmentBalance) || 0);
      }
    } catch (error) {
      console.error('Error fetching PENE balance:', error);
    } finally {
      if (showRefreshing) setBalanceRefreshing(false);
    }
  };

  const fetchActivePlans = async (username) => {
    setPlansLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/payment/GetSubPlanes-web/${username}`);
      if (response.data?.subData && Array.isArray(response.data.subData)) {
        setActivePlans(response.data.subData);
        return;
      }
    } catch {
      // fall through to local data
    }
    // Fallback: use subscription plans stored on the user object
    setActivePlans([]);
    setPlansLoading(false);
  };

  const handleGrowInvestment = async () => {
    setGrowLoading(true);
    try {
      const url = `https://tradebot-k4nt.onrender.com/api/setUpdate/${user.telegramName}`;
      const response = await fetch(url);
      const data = await response.json();
      if (response.status === 200) {
        toast.success('🌱 Investment grown successfully! Your PENE balance has been updated.');
        setCanGrow(false);
        setLastGrowTime(new Date());
        // Re-fetch balance with a short delay to let server finalize the update
        setTimeout(() => fetchPeneBalance(user.telegramName, true), 800);
      } else {
        toast.error(data?.error || 'Failed to grow investment');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setGrowLoading(false);
    }
  };

  const getTimeUntilNextGrow = () => {
    if (!lastGrowTime || canGrow) return null;
    const nextGrow = new Date(lastGrowTime.getTime() + 24 * 60 * 60 * 1000);
    const diff = nextGrow - now;
    if (diff <= 0) { setCanGrow(true); return null; }
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Merge API active plans with user.subscriptionPlan (local context) — dedupe by _id
  const localPlans = (user?.subscriptionPlan || []);
  const allActivePlans = activePlans.length > 0 ? activePlans : localPlans;

  // Compute total monthly earnings from active plans
  const totalMonthlyEarnings = allActivePlans.reduce((sum, plan) => {
    const num = plan.planNumber || plan.deposit || '';
    const meta = PLAN_META[String(num)];
    if (meta) {
      const monthlyStr = meta.monthly.replace(/[^0-9.]/g, '');
      return sum + (parseFloat(monthlyStr) || 0);
    }
    return sum;
  }, 0);

  if (loading || !user) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  const timeRemaining = getTimeUntilNextGrow();

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 mb-3">
            💰 PENE Investment
          </h1>
          <p className="text-slate-600 text-lg">Grow your wealth with our premium meme coin</p>
        </div>

        {/* ── Main Balance Card ── */}
        <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-3xl shadow-2xl p-8 mb-8 text-white">
          <div className="text-center mb-6">
            <p className="text-purple-100 text-sm mb-2">Your PENE Balance</p>
            <div className="relative inline-flex items-center justify-center gap-3">
              <p className={`text-6xl font-bold mb-1 transition-all duration-500 ${balanceRefreshing ? 'opacity-50 scale-95' : 'opacity-100 scale-100'}`}>
                {peneBalance.toLocaleString()}
              </p>
              {balanceRefreshing && (
                <div className="absolute -right-8 top-1 w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>
            <p className="text-purple-100 text-lg font-semibold tracking-widest">PENE</p>
          </div>

          {/* USD balance row */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-purple-100">USD Balance</span>
              <span className="text-2xl font-bold">{formatCurrency(parseAccountBalance(user?.accountBalance))}</span>
            </div>
            {allActivePlans.length > 0 && totalMonthlyEarnings > 0 && (
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/20">
                <span className="text-purple-100 text-sm">Est. monthly earnings</span>
                <span className="text-lg font-bold text-green-300">+${totalMonthlyEarnings.toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-3 gap-3">
            <button onClick={() => navigate('/exchange')}
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all rounded-xl p-4 flex flex-col items-center gap-2 group">
              <DollarSign size={24} className="group-hover:scale-110 transition-transform" />
              <span className="text-sm font-semibold">Exchange</span>
            </button>
            <button onClick={handleGrowInvestment} disabled={!canGrow || growLoading}
              className={`backdrop-blur-sm rounded-xl p-4 flex flex-col items-center gap-2 transition-all group ${
                canGrow && !growLoading ? 'bg-green-500/40 hover:bg-green-500/60' : 'bg-white/10 opacity-50 cursor-not-allowed'
              }`}>
              {growLoading
                ? <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                : <Leaf size={24} className={canGrow ? 'group-hover:scale-110 transition-transform' : ''} />
              }
              <span className="text-xs font-semibold">{timeRemaining || 'Grow'}</span>
            </button>
            <button onClick={() => navigate('/transfer')}
              className="bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all rounded-xl p-4 flex flex-col items-center gap-2 group">
              <Send size={24} className="group-hover:scale-110 transition-transform" />
              <span className="text-sm font-semibold">Send</span>
            </button>
          </div>
        </div>

        {/* ── Active Subscription Plans ── */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Your Active Plans</h2>
              <p className="text-sm text-slate-500 mt-0.5">Subscriptions currently earning for you</p>
            </div>
            {allActivePlans.length > 0 && (
              <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                <CheckCircle size={13} /> {allActivePlans.length} Active
              </span>
            )}
          </div>

          {plansLoading ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-10 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : allActivePlans.length === 0 ? (
            /* Empty state */
            <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
              <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="text-purple-400" size={32} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">No active plans yet</h3>
              <p className="text-slate-500 text-sm mb-5 max-w-xs mx-auto">Subscribe to an investment plan to start earning passive income on your PENE balance.</p>
              <button onClick={() => navigate('/investment')}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-all">
                Browse Plans <ArrowRight size={15} />
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {allActivePlans.map((plan, idx) => {
                const num = String(plan.planNumber || plan.deposit || '');
                const meta = PLAN_META[num] || {};
                const planName = plan.planname || meta.label || `Plan #${num}`;
                const peneEarned = parseFloat(plan.planeAsset || plan.peneAsset || 0);
                const gradient = meta.color || 'from-slate-600 to-slate-700';

                return (
                  <div key={plan._id || idx}
                    className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-all">
                    {/* Plan colour bar */}
                    <div className={`h-1.5 bg-gradient-to-r ${gradient}`}></div>

                    <div className="p-6">
                      {/* Header row */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center flex-shrink-0`}>
                            <BarChart2 className="text-white" size={20} />
                          </div>
                          <div>
                            <p className="font-bold text-slate-900 text-base leading-tight">{planName}</p>
                            {meta.label && <p className="text-xs text-slate-500 mt-0.5">Min. deposit ${Number(num).toLocaleString()}</p>}
                          </div>
                        </div>
                        {meta.badge && (
                          <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                            <Star size={11} />{meta.badge}
                          </span>
                        )}
                      </div>

                      {/* Stats grid */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-slate-50 rounded-xl p-3 text-center">
                          <p className="text-xs text-slate-500 mb-1">ROI</p>
                          <p className="text-lg font-black text-slate-900">{meta.roi || '—'}</p>
                        </div>
                        <div className="bg-green-50 rounded-xl p-3 text-center">
                          <p className="text-xs text-slate-500 mb-1">Monthly</p>
                          <p className="text-lg font-black text-green-700">{meta.monthly || '—'}</p>
                        </div>
                        <div className="bg-purple-50 rounded-xl p-3 text-center">
                          <p className="text-xs text-slate-500 mb-1">PENE Earned</p>
                          <p className="text-lg font-black text-purple-700">{peneEarned.toLocaleString()}</p>
                        </div>
                      </div>

                      {/* Status row */}
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-1.5 text-green-600 text-xs font-semibold">
                          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                          Active & Earning
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <Zap size={12} /><span>Auto-compounding daily</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Summary stats (only if user has plans) ── */}
        {allActivePlans.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center shadow-sm">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="text-purple-600" size={20} />
              </div>
              <p className="text-2xl font-black text-slate-900">{allActivePlans.length}</p>
              <p className="text-xs text-slate-500 mt-1">Active Plans</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center shadow-sm">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <DollarSign className="text-green-600" size={20} />
              </div>
              <p className="text-2xl font-black text-slate-900">${totalMonthlyEarnings.toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-1">Monthly Earnings</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center shadow-sm">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Star className="text-orange-600" size={20} />
              </div>
              <p className="text-2xl font-black text-slate-900">{peneBalance.toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-1">PENE Balance</p>
            </div>
          </div>
        )}

        {/* ── Browse More Plans CTA ── */}
        <div className="bg-white rounded-2xl shadow-sm border-2 border-purple-100 p-6 mb-6 hover:border-purple-300 hover:shadow-md transition-all cursor-pointer group"
             onClick={() => navigate('/investment')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <TrendingUp className="text-white" size={28} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-0.5">
                  {allActivePlans.length > 0 ? 'Add Another Plan' : 'Browse Investment Plans'}
                </h3>
                <p className="text-slate-500 text-sm">Earn 5–18% ROI with plans starting at $1,000</p>
              </div>
            </div>
            <ArrowRight className="text-purple-500 group-hover:translate-x-2 transition-transform flex-shrink-0" size={24} />
          </div>
        </div>

        {/* ── Info Cards ── */}
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 bg-green-500 rounded-xl flex items-center justify-center">
                <Leaf className="text-white" size={22} />
              </div>
              <h3 className="text-base font-bold text-slate-900">24hr Growth</h3>
            </div>
            <p className="text-slate-700 text-sm leading-relaxed">
              Tap <strong>Grow</strong> once every 24 hours to automatically increase your PENE balance based on your active subscription plans.
            </p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 bg-blue-500 rounded-xl flex items-center justify-center">
                <DollarSign className="text-white" size={22} />
              </div>
              <h3 className="text-base font-bold text-slate-900">Exchange Rate</h3>
            </div>
            <p className="text-slate-700 text-sm leading-relaxed">
              Buy or sell PENE at a fixed rate of <strong>$1 per PENE</strong>. Instant transactions with no fees.
            </p>
          </div>
        </div>

        {/* Grow countdown notice */}
        {!canGrow && timeRemaining && (
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Clock className="text-amber-600 flex-shrink-0" size={20} />
              <div>
                <p className="font-semibold text-amber-900">Next Growth Available In</p>
                <p className="text-sm text-amber-700">You can grow again in <strong>{timeRemaining}</strong></p>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PeneInvestmentDashboard;
